import React, { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { useSearchParams } from 'react-router-dom';
import { BusinessCollection } from '../../data-layer/business/constants';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Input } from '../../ui/input/Input';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { Tab, Tabs } from '../../ui/navigation/Tabs';
import { Typography } from '../../ui/typography/Typography';
import { isSuperAdmin } from '../../utils/accessService';
import { getWindow } from '../../utils/getWindow';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { useBusinessCollection } from '../business/useBusinessCollection';
import { usePostCollection } from '../post/usePostCollection';
import { useCurrentUser } from '../users/useCurrentUser';
import { SearchBusiness } from './SearchBusiness';
import { SearchPost } from './SearchPost';

const TIMEOUT = 250;
const PER_PAGE = 24;

export const Search = () => {
    const [query, setQuery] = useSearchParams();
    const fulltextQuery = query.get('query') ?? '';
    const searchTypeQuery = query.get('searchType') ?? 'post';
    const currentUser = useCurrentUser();
    const t = useTranslation();

    const onChangeQuery = useCallback(
        (queryText: string) =>
            setQuery({ searchType: searchTypeQuery, query: queryText }, { replace: true }),
        [setQuery, searchTypeQuery],
    );

    const onChangeTab = useCallback(
        (newSearchType: string) => {
            setQuery({ searchType: newSearchType, query: fulltextQuery }, { replace: true });
        },
        [setQuery, fulltextQuery],
    );

    const businessCollection = useBusinessCollection({
        namespace: BusinessCollection.SEARCH,
    });
    const postCollection = usePostCollection({
        namespace: 'search-collection',
        filters: {
            amount: PER_PAGE,
            query: fulltextQuery,
        },
    });

    const onBusinessSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    businessCollection.onLoadNext({ query: trim(queryText), limit: 20 }),
                TIMEOUT,
            ),
        [businessCollection.onLoadNext],
    );

    const onPostSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    postCollection.onUpdateQuery({
                        amount: PER_PAGE,
                        query: queryText,
                    }),
                TIMEOUT,
            ),
        [postCollection.onUpdateQuery],
    );

    useEffect(() => {
        if (
            businessCollection.filters.query !== trim(fulltextQuery) &&
            !businessCollection.isLoading
        ) {
            onBusinessSearch(fulltextQuery);
        }
    }, [
        fulltextQuery,
        businessCollection.isLoading,
        businessCollection.filters.query,
        onBusinessSearch,
    ]);

    useEffect(() => {
        if (postCollection.filters?.query !== trim(fulltextQuery) && !postCollection.isLoading) {
            onPostSearch(fulltextQuery);
        }
    }, [
        fulltextQuery,
        postCollection,
        postCollection.onUpdateQuery,
        postCollection.isLoading,
        onPostSearch,
    ]);

    useEffect(() => () => {
        getWindow()?.sessionStorage.setItem('lastSearch', fulltextQuery ?? '');
    });

    useMeta({ title: 'Поиск • Яндекс Ритм' });

    return (
        <DashboardPage>
            <Flex
                p={2}
                width="100%"
                direction="column"
                alignItems="stretch"
                borderRadius={{ sm: 2 }}
                minHeight={{ xs: '100%', sm: '85%' }}
                background="var(--background-primary)"
            >
                <Box px={1} pb={1}>
                    <Input
                        value={query.get('query') ?? ''}
                        variant="filled"
                        placeholder="Поиск"
                        endAdornment={
                            query.get('query') ? (
                                <Box alignSelf="center">
                                    <IconButton
                                        size="xxs"
                                        variant="filled"
                                        onClick={() => onChangeQuery('')}
                                    >
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            ) : undefined
                        }
                        onInput={(event) => onChangeQuery(event.currentTarget.value)}
                    />
                </Box>
                {isSuperAdmin(currentUser?.role) && (
                    <Tabs px={{ xs: 1, sm: 3 }} value={searchTypeQuery} onChange={onChangeTab}>
                        <Tab width="50%" value="post" textDecoration="none">
                            <Typography
                                color="inherit"
                                variant="body"
                                weight="medium"
                                textAlign="center"
                            >
                                {t('Посты')}
                            </Typography>
                        </Tab>
                        <Tab width="50%" value="business" textDecoration="none">
                            <Typography
                                color="inherit"
                                variant="body"
                                weight="medium"
                                textAlign="center"
                            >
                                {t('Профили')}
                            </Typography>
                        </Tab>
                    </Tabs>
                )}
                {searchTypeQuery === 'post' ? (
                    <SearchPost postCollection={postCollection} />
                ) : (
                    <SearchBusiness />
                )}
            </Flex>
        </DashboardPage>
    );
};
